"use client";

import { useState } from "react";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  LineChart,
  type LineRow,
} from "@/components/tool-kit/charts/line-chart";
import {
  Checkbox,
  DataTable,
  Notice,
  type Option,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CurrencyField, useMoney } from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  parseIntegerInput,
  parseNumberInput,
  parseNumberList,
} from "@/lib/parse-number";
import {
  type CompareRow,
  compareMethods,
  DEPRECIATION_METHODS,
  type DepreciationMethod,
  dbExpenses,
  dbRate,
  ddbExpenses,
  MAX_LIFE,
  rowsFromExpenses,
  straightLineExpenses,
  sydExpenses,
  unitsExpenses,
} from "@/lib/tool-kit/accounting/depreciation";
import { chartFilename } from "@/lib/tool-kit/charts/layout";

/** English strings. A translated content file overrides these through `ui`. */
export const DEPRECIATION_CALCULATOR_STRINGS = {
  inputsHeading: "The asset",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  methodLabel: "Method",
  methodStraightLine: "Straight-line (Excel SLN)",
  methodDeclining: "Declining balance, fixed rate (Excel DB)",
  methodDouble: "Double-declining balance (Excel DDB)",
  methodSum: "Sum-of-the-years'-digits (Excel SYD)",
  methodUnits: "Units of production",
  cost: "Cost of the asset",
  salvage: "Salvage (residual) value",
  life: "Useful life (whole years)",
  month: "Months of depreciation in year 1 (1–12)",
  monthHint:
    "Excel's DB prorates the first year by this many months and adds a final partial year. Use 12 for a full first year.",
  factor: "Declining-balance factor",
  factorHint: "2 gives double-declining; 1.5 gives 150% declining balance.",
  switchLine: "Switch to straight-line when it gives a larger charge",
  totalUnits: "Total units over the asset's life",
  usedLabel: "Units used each period — one per line",
  usedHint: "Hours, miles, items produced. One line per period, in order.",
  convention:
    "Each period is a full year, except year 1 under the fixed-rate declining-balance method. Book value never falls below salvage under straight-line, double-declining and units-of-production.",
  resultsHeading: "Results",
  depreciable: "Depreciable amount (cost − salvage)",
  firstExpense: "Period 1 expense",
  totalExpense: "Total depreciation",
  endingBook: "Ending book value",
  rateStraight: "Annual rate (1 ÷ life)",
  rateDeclining: "Rate, rounded to 3 decimals",
  rateDouble: "Annual rate (factor ÷ life)",
  rateUnits: "Depreciation per unit",
  workingHeading: "The working for period 1",
  workStraight:
    "Expense = (cost − salvage) ÷ life = ({cost} − {salvage}) ÷ {life} = {expense}",
  workDeclining:
    "Rate = 1 − (salvage ÷ cost)^(1 ÷ life) = {raw}, rounded to {rate}. Period 1 = cost × rate × {months} ÷ 12 = {expense}",
  workDouble:
    "Expense = book value × factor ÷ life = {cost} × {factor} ÷ {life} = {expense}",
  workSum:
    "Sum of the years' digits = life × (life + 1) ÷ 2 = {sum}. Period 1 = (cost − salvage) × {life} ÷ {sum} = {expense}",
  workUnits:
    "Per unit = (cost − salvage) ÷ total units = ({cost} − {salvage}) ÷ {units} = {rate}. Period 1 = {rate} × {used} = {expense}",
  scheduleHeading: "Depreciation schedule",
  periodColumn: "Period",
  unitsColumn: "Units",
  expenseColumn: "Depreciation expense",
  accumulatedColumn: "Accumulated depreciation",
  bookColumn: "Book value at period end",
  compareHeading: "The four time-based methods side by side",
  straightColumn: "Straight-line",
  decliningColumn: "Declining balance (DB)",
  doubleColumn: "Declining (DDB)",
  sumColumn: "Sum-of-years'-digits",
  chartTitle: "Book value by method",
  chartXTitle: "Period",
  chartYTitle: "Book value",
  chartLabel: "Book value by depreciation method",
  chartDescription:
    "Line chart of book value from period 0 to period {last} under straight-line, declining-balance, double-declining and sum-of-the-years'-digits depreciation, starting at {cost}.",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  salvageZero:
    "With a salvage value of zero Excel's DB rate is 100%, so the whole cost is charged in period 1. Use a small positive salvage, or another method.",
  overUnits:
    "The units entered exceed the total units, so depreciation stops once book value reaches salvage.",
  needInputs:
    "Enter a cost above zero, a salvage value of zero or more and below the cost, and a whole-number life from 1 to {max}.",
  needUnits:
    "Enter total units above zero and the units used in each period, one per line.",
  needMonths: "Enter a whole number of months from 1 to 12.",
  needFactor: "Enter a factor above zero.",
} as const;

type Strings = typeof DEPRECIATION_CALCULATOR_STRINGS;

const METHOD_LABELS: Record<DepreciationMethod, keyof Strings> = {
  "straight-line": "methodStraightLine",
  "declining-balance": "methodDeclining",
  "double-declining": "methodDouble",
  "sum-of-years": "methodSum",
  units: "methodUnits",
};

const EXAMPLE_USED = "20000\n30000\n25000\n15000\n10000";

interface Config {
  method: DepreciationMethod;
  cost: number;
  salvage: number;
  life: number;
  month: number;
  factor: number;
  switchLine: boolean;
  totalUnits: number;
  used: number[];
}

interface Form {
  method: DepreciationMethod;
  cost: string;
  salvage: string;
  life: string;
  month: string;
  factor: string;
  switchLine: boolean;
  totalUnits: string;
  used: string;
}

/** The parsed form, or the name of the string that explains what is wrong. */
function readForm(form: Form): Config | keyof Strings {
  const cost = parseNumberInput(form.cost);
  const salvage = parseNumberInput(form.salvage);
  const life = parseIntegerInput(form.life);
  const month = parseIntegerInput(form.month);
  const factor = parseNumberInput(form.factor);
  const totalUnits = parseNumberInput(form.totalUnits);
  const used = parseNumberList(form.used).filter((v) => v >= 0);
  const base = {
    method: form.method,
    switchLine: form.switchLine,
    used,
    totalUnits: totalUnits ?? 0,
    factor: factor ?? 2,
    month: month ?? 12,
    life: life ?? 0,
    cost: cost ?? 0,
    salvage: salvage ?? 0,
  };
  if (
    cost === null ||
    cost <= 0 ||
    salvage === null ||
    salvage < 0 ||
    salvage >= cost
  ) {
    return "needInputs";
  }
  if (form.method === "units") {
    return totalUnits !== null && totalUnits > 0 && used.length > 0
      ? base
      : "needUnits";
  }
  if (life === null || life < 1 || life > MAX_LIFE) {
    return "needInputs";
  }
  if (
    form.method === "declining-balance" &&
    (month === null || month < 1 || month > 12)
  ) {
    return "needMonths";
  }
  if (form.method === "double-declining" && (factor === null || factor <= 0)) {
    return "needFactor";
  }
  return base;
}

function expensesFor(c: Config): number[] {
  switch (c.method) {
    case "straight-line":
      return straightLineExpenses(c.cost, c.salvage, c.life);
    case "declining-balance":
      return dbExpenses(c.cost, c.salvage, c.life, c.month);
    case "double-declining":
      return ddbExpenses(c.cost, c.salvage, c.life, c.factor, c.switchLine);
    case "sum-of-years":
      return sydExpenses(c.cost, c.salvage, c.life);
    default:
      return unitsExpenses(c.cost, c.salvage, c.totalUnits, c.used);
  }
}

function useWorking(
  c: Config,
  first: number,
  strings: Strings,
  currency: string
) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const num = (v: number, max = 4) => fmt(v, { maximumFractionDigits: max });
  const base = {
    cost: money(c.cost),
    salvage: money(c.salvage),
    life: fmt(c.life),
    expense: money(first),
  };
  switch (c.method) {
    case "straight-line":
      return t(strings.workStraight, base);
    case "declining-balance":
      return t(strings.workDeclining, {
        ...base,
        raw: num(1 - (c.salvage / c.cost) ** (1 / c.life), 6),
        rate: num(dbRate(c.cost, c.salvage, c.life), 3),
        months: fmt(c.month),
      });
    case "double-declining":
      return t(strings.workDouble, { ...base, factor: num(c.factor) });
    case "sum-of-years":
      return t(strings.workSum, {
        ...base,
        sum: fmt((c.life * (c.life + 1)) / 2),
      });
    default: {
      const rate = (c.cost - c.salvage) / c.totalUnits;
      return t(strings.workUnits, {
        ...base,
        units: fmt(c.totalUnits),
        rate: money(rate),
        used: fmt(c.used[0] ?? 0),
      });
    }
  }
}

function rateCard(
  c: Config,
  strings: Strings,
  fmtPct: (v: number) => string,
  money: (v: number) => string
) {
  switch (c.method) {
    case "straight-line":
      return { label: strings.rateStraight, value: fmtPct(1 / c.life) };
    case "declining-balance":
      return {
        label: strings.rateDeclining,
        value: fmtPct(dbRate(c.cost, c.salvage, c.life)),
      };
    case "double-declining":
      return { label: strings.rateDouble, value: fmtPct(c.factor / c.life) };
    case "units":
      return {
        label: strings.rateUnits,
        value: money((c.cost - c.salvage) / c.totalUnits),
      };
    default:
      return null;
  }
}

function BookChart({
  compare,
  cost,
  strings,
}: {
  compare: CompareRow[];
  cost: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const names = [
    strings.straightColumn,
    strings.decliningColumn,
    strings.doubleColumn,
    strings.sumColumn,
  ];
  const keys = ["straightLine", "declining", "double", "sumOfYears"] as const;
  const running = [cost, cost, cost, cost];
  const rows: LineRow[] = [{ label: "0", x: 0, values: [...running] }];
  for (const row of compare) {
    keys.forEach((key, i) => {
      running[i] -= row[key];
    });
    rows.push({ label: fmt(row.period), x: row.period, values: [...running] });
  }
  const style = {
    title: strings.chartTitle,
    xTitle: strings.chartXTitle,
    yTitle: strings.chartYTitle,
    color: "blue" as const,
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  };
  return (
    <ChartFigure
      apa={false}
      data={toTsv([
        [strings.chartXTitle, ...names],
        ...rows.map((r) => [r.label, ...r.values]),
      ])}
      figureNumber="1"
      filename={chartFilename(strings.chartTitle, "book-value")}
      labels={strings}
      note=""
      table={null}
      title={strings.chartTitle}
    >
      <LineChart
        description={t(strings.chartDescription, {
          last: fmt(compare.length),
          cost: fmt(cost, { maximumFractionDigits: 2 }),
        })}
        fmt={fmt}
        id="dep-chart"
        label={strings.chartLabel}
        markers={false}
        names={names}
        numeric
        rows={rows}
        style={style}
        zero
      />
    </ChartFigure>
  );
}

function Compare({
  c,
  currency,
  strings,
}: {
  c: Config;
  currency: string;
  strings: Strings;
}) {
  const money = useMoney(currency);
  const compare = compareMethods(c.cost, c.salvage, c.life, {
    month: c.method === "declining-balance" ? c.month : 12,
    factor: c.factor,
    switchToStraightLine: c.switchLine,
  });
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      <BookChart compare={compare} cost={c.cost} strings={strings} />
      <DataTable
        head={[
          strings.periodColumn,
          strings.straightColumn,
          strings.decliningColumn,
          strings.doubleColumn,
          strings.sumColumn,
        ]}
        rows={compare.map((r) => [
          r.period,
          money(r.straightLine),
          money(r.declining),
          money(r.double),
          money(r.sumOfYears),
        ])}
      />
    </>
  );
}

function Results({
  c,
  currency,
  strings,
}: {
  c: Config;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const expenses = expensesFor(c);
  const rows = rowsFromExpenses(c.cost, expenses);
  const last = rows.at(-1);
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 3 })}%`;
  const rate = rateCard(c, strings, pct, money);
  const working = useWorking(c, expenses[0] ?? 0, strings, currency);
  const units = c.method === "units";
  const overUnits = units && c.used.reduce((a, b) => a + b, 0) > c.totalUnits;
  const head = [
    strings.periodColumn,
    ...(units ? [strings.unitsColumn] : []),
    strings.expenseColumn,
    strings.accumulatedColumn,
    strings.bookColumn,
  ];
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.firstExpense}
          value={money(expenses[0] ?? 0)}
        />
        <StatCard
          label={strings.depreciable}
          value={money(c.cost - c.salvage)}
        />
        <StatCard
          label={strings.totalExpense}
          value={money(last?.accumulated ?? 0)}
        />
        <StatCard
          label={strings.endingBook}
          value={money(last?.bookValue ?? c.cost)}
        />
        {rate ? <StatCard label={rate.label} value={rate.value} /> : null}
      </StatGrid>
      {c.method === "declining-balance" && c.salvage === 0 ? (
        <Notice>{strings.salvageZero}</Notice>
      ) : null}
      {overUnits ? <Notice>{strings.overUnits}</Notice> : null}
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {working}
        </p>
      </div>
      <h3 className={WIDGET_H3}>{strings.scheduleHeading}</h3>
      <DataTable
        head={head}
        rows={rows.map((r, i) => [
          r.period,
          ...(units ? [fmt(c.used[i] ?? 0)] : []),
          money(r.expense),
          money(r.accumulated),
          money(r.bookValue),
        ])}
      />
      {units ? null : <Compare c={c} currency={currency} strings={strings} />}
    </section>
  );
}

export default function DepreciationCalculatorWidget() {
  const strings = useToolUi(DEPRECIATION_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState<Form>({
    method: "straight-line",
    cost: "10000",
    salvage: "1000",
    life: "5",
    month: "12",
    factor: "2",
    switchLine: false,
    totalUnits: "100000",
    used: EXAMPLE_USED,
  });
  const update = (patch: Partial<Form>) =>
    setForm((old) => ({ ...old, ...patch }));
  const options: Option<DepreciationMethod>[] = DEPRECIATION_METHODS.map(
    (m) => ({
      value: m,
      label: strings[METHOD_LABELS[m]],
    })
  );
  const config = readForm(form);
  const units = form.method === "units";

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <SelectField
          id="dep-method"
          label={strings.methodLabel}
          onChange={(method) => update({ method })}
          options={options}
          value={form.method}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="dep-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            id="dep-cost"
            label={strings.cost}
            numeric
            onChange={(cost) => update({ cost })}
            value={form.cost}
          />
          <TextField
            id="dep-salvage"
            label={strings.salvage}
            numeric
            onChange={(salvage) => update({ salvage })}
            value={form.salvage}
          />
          {units ? (
            <TextField
              id="dep-total"
              label={strings.totalUnits}
              numeric
              onChange={(totalUnits) => update({ totalUnits })}
              value={form.totalUnits}
            />
          ) : (
            <TextField
              id="dep-life"
              label={strings.life}
              numeric
              onChange={(life) => update({ life })}
              value={form.life}
            />
          )}
          {form.method === "declining-balance" ? (
            <TextField
              hint={strings.monthHint}
              id="dep-month"
              label={strings.month}
              numeric
              onChange={(month) => update({ month })}
              value={form.month}
            />
          ) : null}
          {form.method === "double-declining" ? (
            <TextField
              hint={strings.factorHint}
              id="dep-factor"
              label={strings.factor}
              numeric
              onChange={(factor) => update({ factor })}
              value={form.factor}
            />
          ) : null}
        </div>
        {form.method === "double-declining" ? (
          <Checkbox
            checked={form.switchLine}
            label={strings.switchLine}
            onChange={(switchLine) => update({ switchLine })}
          />
        ) : null}
        {units ? (
          <TextAreaField
            hint={strings.usedHint}
            id="dep-used"
            label={strings.usedLabel}
            onChange={(used) => update({ used })}
            rows={5}
            value={form.used}
          />
        ) : null}
        <p className={HINT}>{strings.convention}</p>
      </section>
      {typeof config === "string" ? (
        <Notice>{t(strings[config], { max: String(MAX_LIFE) })}</Notice>
      ) : (
        <Results c={config} currency={currency} strings={strings} />
      )}
    </div>
  );
}
